/**
 * admin/brand.js — who this admin panel belongs to.
 *
 * The panel is one build deployed once per product: the Forge app points
 * at the Forge backend, the Makua app at the Makua one. Rather than being
 * told at build time, it asks the API which brand it's wearing, so a third
 * product would need no frontend change at all.
 *
 * `/auth/identity` is public, because the login screen needs the name and
 * colours before anyone has a token. It carries nothing that isn't already
 * on the public website.
 */
import { useState, useEffect } from "react";
import { authAPI } from "../services/api";

/* What to show before the API answers, and if it never does. Forge is the
   safe default: it's the deployment that has existed all along. */
export const FALLBACK = {
  product: "forge",
  name: "Forge Retreat Colombia",
  short: "Forge",
  supportEmail: "support@forge.ngo",
  tagline: "ONE JOURNEY · SAME PATH FORWARD",
  colors: { rust: "#b8441a", rustDk: "#9a3915", rustSoft: "#d0633a", forest: "#1a2818" },
};

let _cache = null;
let _inflight = null;

/**
 * "Forge Retreat Colombia" → ["FORGE", "RETREAT", "COLOMBIA"]
 * "Makua Retreats"        → ["MAKUA", "RETREATS"]
 *
 * The first word is the big one in the sidebar, the rest sit under it.
 */
export const brandWords = (name) =>
  String(name || "").trim().toUpperCase().split(/\s+/).filter(Boolean).slice(0, 3);

/**
 * Paint the accent colours onto the document.
 *
 * Only the two brand accents — the panel's creams, greys and status
 * colours are tuned for legibility and stay exactly as they are, whichever
 * brand is wearing them.
 */
const applyColors = (colors) => {
  if (!colors) return;
  const root = document.documentElement;
  if (colors.rust) {
    root.style.setProperty("--admin-rust", colors.rust);
    // Exact values when the brand names them; derived only as a fallback,
    // so neither panel's hover shades drift by a point or two.
    root.style.setProperty("--admin-rust-dk", colors.rustDk || shade(colors.rust, -0.15));
    root.style.setProperty("--admin-rust-soft", colors.rustSoft || shade(colors.rust, 0.22));
    const rgb = channels(colors.rust);
    if (rgb) root.style.setProperty("--admin-rust-rgb", rgb);
  }
  if (colors.forest) {
    root.style.setProperty("--admin-forest", colors.forest);
    root.style.setProperty("--admin-sidebar", colors.forest);
  }
};

/** "#a65d2b" → "166, 93, 43", for the rgba() tints the panel builds. */
function channels(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** Darken (or lighten) a #rrggbb by a fraction, for the hover shade. */
function shade(hex, amount) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const v = Math.round(c + (amount < 0 ? c : 255 - c) * amount);
    return Math.max(0, Math.min(255, v));
  });
  return `#${ch.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

const shape = (data) => ({
  product: data.product || FALLBACK.product,
  name: data.brand?.name || FALLBACK.name,
  short: data.brand?.short || FALLBACK.short,
  supportEmail: data.brand?.supportEmail || FALLBACK.supportEmail,
  tagline: data.brand?.tagline || FALLBACK.tagline,
  colors: data.brand?.colors || FALLBACK.colors,
});

/**
 * The brand this panel is wearing. Cached for the tab's lifetime — it
 * cannot change without a redeploy.
 */
export function useBrand() {
  const [brand, setBrand] = useState(_cache || FALLBACK);

  useEffect(() => {
    let alive = true;
    if (_cache) { applyColors(_cache.colors); return undefined; }

    if (!_inflight) {
      _inflight = authAPI
        .identity()
        .then((res) => {
          if (res?.data) { _cache = shape(res.data); applyColors(_cache.colors); }
          return _cache;
        })
        .catch(() => null);   // an older API, or offline — Forge's defaults stand
    }

    _inflight.then((result) => { if (alive && result) setBrand(result); });
    return () => { alive = false; };
  }, []);

  /* The browser tab, too — two admin tabs open at once are otherwise
     impossible to tell apart. */
  useEffect(() => {
    document.title = `${brand.short} Admin`;
  }, [brand.short]);

  return brand;
}
